import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React from 'react';
import { useSelector } from 'react-redux';
import Loader from 'src/components/Loader';
import { emailList, maxLength, minPhoneLength, required, Validators } from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import styles from './assets/students-form.module.scss';
import { faSave, faBan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { asyncPersistStudent } from 'src/store/actions/student-actions';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { DropZoneField } from 'src/components/DropZoneField';
import { FieldInputMask } from 'src/components/redux-form/fields/FieldInputMask';
import { FieldDropdown } from 'src/components/redux-form/fields/FieldDropdown';
import { miniLoaderForSelector } from 'src/store/selectors/students-selectors';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';

export interface StudentFormData {
    id: string | number;
    firstName: string;
    lastName: string;
    code: string;
    address: string;
    parentFirstName: string;
    parentLastName: string;
    parentPrimaryEmail: string;
    parentSecondaryEmail: string;
    parentPhone: string;
    attorneyFullName: string;
    attorneyPrimaryEmail: string;
    attorneySecondaryEmail: string;
    attorneyPhoneNumber: string;
    dob: string;
    status: any;
    orderDocName: string | null;
    orderDocMimeType: string | null;
    consentDocName: string | null;
    consentDocMimeType: string | null;

    parentConsentDocName: string | null;
    parentConsentDocMimeType: string | null;
}

interface StudentFormProps {
    orderDoc: any;
    setOrderDoc: (it: any) => void;
    removeOrderDoc: (it: any) => void;
    consentDoc: any;
    parentConsentDoc: any;
    handleSub: (data: (e: any) => void, e: any) => void;
    statuses: { label: string; value: number | string }[];
    setConsentDoc: (it: any) => void;
    removeConsentDoc: (it: any) => void;
    setParentConsentDoc: (it: any) => void;
    removeParentConsentDoc: (it: any) => void;
    files: any;
    setFiles: (it: []) => void;
    removeFile: (it: any) => void;
}

type InjectedProps = InjectedFormProps<StudentFormData, StudentFormProps>;

const validators: Validators<StudentFormData> = {
    firstName: [required({ message: 'Student First Name is required.' }), maxLength({ max: 255 })],
    lastName: [required({ message: 'Student Last Name is required.' }), maxLength({ max: 255 })],
    code: [required({ message: 'Student ID is required.' }), maxLength({ max: 255 })],
    address: [required({ message: 'Home Address is required.' }), maxLength({ max: 255 })],
    parentFirstName: [required({ message: 'Parent First Name is required.' }), maxLength({ max: 255 })],
    parentLastName: [required({ message: 'Parent Last Name is required.' }), maxLength({ max: 255 })],
    parentPrimaryEmail: [
        required({ message: 'Parent Primary Email is required.' }),
        emailList(),
        maxLength({ max: 255 }),
    ],
    parentSecondaryEmail: [maxLength({ max: 255 }), emailList()],
    parentPhone: [required({ message: 'Parent Phone is required.' }), minPhoneLength({ min: 14 })],
    attorneyFullName: [required({ message: 'Attorney Full Name is required.' }), maxLength({ max: 255 })],
    dob: [required({ message: 'Date of Birth is required.' }), maxLength({ max: 255 })],
    attorneyPrimaryEmail: [
        required({ message: 'Attorney Primary Email is required.' }),
        maxLength({ max: 255 }),
        emailList(),
    ],
    attorneySecondaryEmail: [maxLength({ max: 255 }), emailList()],
    attorneyPhoneNumber: [minPhoneLength({ min: 14 })],
};

const StudentForm: React.FunctionComponent<InjectedProps & StudentFormProps> = ({
    handleSubmit,
    error,
    dirty,
    consentDoc,
    parentConsentDoc,

    setConsentDoc,
    setParentConsentDoc,
    handleSub,

    statuses,
    submitting,
    initialValues,
    files,
    setFiles,
    removeFile,
}: InjectedProps & StudentFormProps) => {
    const isNew = !initialValues || !initialValues.id;
    const miniLoaderFor = useSelector(miniLoaderForSelector);

    const consentDocFiles = [];
    if (consentDoc) {
        const consentDocFile = {
            attach: consentDoc,
            mimeType: initialValues.consentDocMimeType,
            name: initialValues.consentDocName,
        };
        consentDocFiles.push(consentDocFile);
    }
    const parentConsentDocFiles = [];
    if (parentConsentDoc) {
        const parentConsentDocFile = {
            attach: parentConsentDoc,
            mimeType: initialValues.parentConsentDocMimeType,
            name: initialValues.parentConsentDocName,
        };
        parentConsentDocFiles.push(parentConsentDocFile);
    }

    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <FormError error={error} />
            <FieldInput
                name="id"
                type="hidden"
                wrapperConfig={{
                    classes: {
                        inputWrapper: 'm-0',
                    },
                }}
            />
            <FieldInput
                name="firstName"
                type="text"
                validate={validators.firstName}
                wrapperConfig={{
                    label: 'First Name *',
                }}
            />
            <FieldInput
                name="lastName"
                type="text"
                validate={validators.lastName}
                wrapperConfig={{
                    label: 'Last Name *',
                }}
            />
            <FieldInput
                name="code"
                type="text"
                validate={validators.code}
                wrapperConfig={{
                    label: 'Student ID *',
                }}
            />
            <FieldInput
                name="address"
                type="text"
                validate={validators.address}
                wrapperConfig={{
                    label: 'Home Address *',
                }}
            />

            <FieldDate
                name="dob"
                type="text"
                date={true}
                validate={validators.dob}
                wrapperConfig={{
                    label: 'Date of birth *',
                }}
            />
            <FieldInput
                name="parentFirstName"
                type="text"
                validate={validators.parentFirstName}
                wrapperConfig={{
                    label: 'Parent First Name *',
                }}
            />
            <FieldInput
                name="parentLastName"
                type="text"
                validate={validators.parentLastName}
                wrapperConfig={{
                    label: 'Parent Last Name *',
                }}
            />

            <FieldInput
                name="parentPrimaryEmail"
                type="text"
                validate={validators.parentPrimaryEmail}
                wrapperConfig={{
                    label: 'Parent Primary Email *',
                }}
            />
            <FieldInput
                name="parentSecondaryEmail"
                type="text"
                validate={validators.parentSecondaryEmail}
                wrapperConfig={{
                    label: 'Parent Secondary Email(s)',
                }}
            />
            <FieldInputMask
                mask={'+1 999-999-9999'}
                name="parentPhone"
                type="phone"
                validate={validators.parentPhone}
                wrapperConfig={{
                    label: 'Parent Phone Number *',
                }}
            />
            <FieldInput
                name="attorneyFullName"
                type="text"
                validate={validators.attorneyFullName}
                wrapperConfig={{
                    label: 'Attorney Full Name *',
                }}
            />
            <FieldInput
                name="attorneyPrimaryEmail"
                type="text"
                validate={validators.attorneyPrimaryEmail}
                wrapperConfig={{
                    label: 'Attorney Primary Email *',
                }}
            />
            <FieldInput
                name="attorneySecondaryEmail"
                type="text"
                validate={validators.attorneySecondaryEmail}
                wrapperConfig={{
                    label: 'Attorney Secondary Email(s)',
                }}
            />
            <FieldInputMask
                mask={'+1 999-999-9999'}
                name="attorneyPhoneNumber"
                type="phone"
                validate={validators.attorneyPhoneNumber}
                wrapperConfig={{
                    label: 'Attorney Phone Number ',
                }}
            />
            <FieldDropdown name="status" data={statuses} textField={'label'} valueField={'value'} />

            <div className={'position-relative'}>
                {miniLoaderFor['FILE'] ? <Loader noFull={true} /> : null}

                <div>
                    <div>Parent consent for telehealth services:</div>

                    <DropZoneField single={true} files={consentDocFiles} setFiles={setConsentDoc} />
                </div>
                <div>
                    <div>Parent consent for authorization change:</div>

                    <DropZoneField single={true} files={parentConsentDocFiles} setFiles={setParentConsentDoc} />
                </div>
                <div>
                    <div>Other Supporting Documents:</div>

                    <DropZoneField
                        files={files}
                        setFiles={setFiles}
                        removeFile={(id: string | number) => removeFile(id)}
                    />
                </div>
            </div>
            <div className={classNames(styles.actions)}>
                <button
                    type="button"
                    onClick={(e) => {
                        handleSub(handleSubmit, e);
                    }}
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> {isNew ? 'Create' : 'Update'}
                </button>
                <Link className={classNames('btn', 'btn-danger')} to={Router.generate(routes.STUDENTS_LIST)}>
                    <FontAwesomeIcon icon={faBan} /> Cancel
                </Link>
            </div>
        </form>
    );
};

export default reduxForm<StudentFormData, StudentFormProps>({
    form: 'student-form',
    enableReinitialize: true,
    onSubmit: async (data: StudentFormData, dispatch: Dispatch<any>, props: StudentFormProps) => {
        await asyncPersistStudent(
            { ...data, status: data.status.value },
            {
                consentDoc: props.consentDoc,
                parentConsentDoc: props.parentConsentDoc,
                orderDoc: props.orderDoc,
                files: props.files,
            },
            dispatch,
        );
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<StudentFormData, StudentFormProps, string>,
    ) {
        const isNew = !props.initialValues || !props.initialValues.id;

        toast.success(`Student was successfully ${isNew ? 'created' : 'updated'}.`);
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(StudentForm);
